import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { usd, ddmmmyy } from "@/lib/format";

const WINDOWS = [
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
  { value: 180, label: "Last 6 months" },
  { value: 365, label: "Last 12 months" },
  { value: 0, label: "All time" },
];

const escapeCsv = (v) => {
  const s = v == null ? "" : String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

export const MissingPriceBacklog = () => {
  const navigate = useNavigate();
  const [days, setDays] = useState(90);
  const [data, setData] = useState({ rows: [], count: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await apiClient.get("/dashboard/missing-price-backlog", {
          params: { days },
        });
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData({ rows: [], count: 0 });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  const exportCsv = () => {
    const header = [
      "Part Number",
      "Description",
      "Occurrences",
      "Missing Prices (USD)",
      "Last Shipment Ref",
      "Last Customer",
      "Last Seen",
    ];
    const rows = data.rows.map((r) => [
      r.part_number,
      r.description || "",
      r.occurrences,
      (r.missing_prices || [])
        .map((p) => `${p.price.toFixed(2)} (x${p.count})`)
        .join("; "),
      r.last_seen?.reference || "",
      r.last_seen?.customer_name || "",
      r.last_seen?.uploaded_at ? r.last_seen.uploaded_at.slice(0, 10) : "",
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map(escapeCsv).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const label =
      WINDOWS.find((w) => w.value === days)?.label.replace(/\s+/g, "-") || "all";
    a.href = url;
    a.download = `missing-price-backlog-${label}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="bg-white rounded-md"
      style={{ border: "1px solid var(--color-border)" }}
      data-testid="missing-price-backlog"
    >
      <div
        className="px-4 py-3 border-b flex flex-wrap items-center gap-3"
        style={{ borderColor: "var(--color-border)" }}
      >
        <div>
          <div className="text-xs uppercase tracking-wider font-semibold text-[#0F2044]">
            Missing price backlog
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5">
            Parts with invoice prices not in the Parts-Tariff Master — duty was
            back-calculated for these lines
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <select
            data-testid="mpb-window"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-1.5 rounded-md text-sm bg-white"
            style={{ border: "1px solid var(--color-border)" }}
          >
            {WINDOWS.map((w) => (
              <option key={w.value} value={w.value}>
                {w.label}
              </option>
            ))}
          </select>
          <button
            data-testid="mpb-export-csv"
            onClick={exportCsv}
            disabled={!data.rows.length}
            className="px-3 py-1.5 rounded-md text-sm bg-[#0F2044] text-white disabled:opacity-40"
          >
            Export CSV
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="dr-table w-full text-sm">
          <thead>
            <tr>
              <th>Part No.</th>
              <th>Description</th>
              <th className="text-right">Occurrences</th>
              <th>Missing prices</th>
              <th>Last shipment</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.slice(0, 25).map((r) => (
              <tr key={r.part_number} data-testid={`mpb-row-${r.part_number}`}>
                <td className="font-mono text-xs">{r.part_number}</td>
                <td className="text-xs text-gray-700">{r.description || "—"}</td>
                <td className="num font-semibold">{r.occurrences}</td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {(r.missing_prices || []).map((p) => (
                      <span
                        key={p.price}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-amber-50 text-amber-800"
                        style={{ border: "1px solid #FCD34D" }}
                      >
                        <span className="font-mono">{usd(p.price)}</span>
                        <span className="text-amber-700">×{p.count}</span>
                      </span>
                    ))}
                  </div>
                </td>
                <td>
                  {r.last_seen ? (
                    <button
                      onClick={() =>
                        navigate(`/shipments/${r.last_seen.reference}`)
                      }
                      className="text-left hover:underline"
                      data-testid={`mpb-last-${r.part_number}`}
                    >
                      <div className="font-mono text-xs text-[#0F2044]">
                        {r.last_seen.reference}
                      </div>
                      <div className="text-[11px] text-gray-500">
                        {r.last_seen.customer_name} ·{" "}
                        <span className="uppercase-date">
                          {ddmmmyy(r.last_seen.uploaded_at)}
                        </span>
                      </div>
                    </button>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
            {!data.rows.length && !loading && (
              <tr>
                <td
                  colSpan="5"
                  className="text-center text-gray-500 py-8"
                  data-testid="mpb-empty"
                >
                  No missing prices in this window — every invoice line matched
                  the master exactly.
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan="5" className="text-center text-gray-500 py-8">
                  Loading…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {data.rows.length > 25 && (
        <div className="px-4 py-2 text-[11px] text-gray-500 border-t" style={{ borderColor: "var(--color-border)" }}>
          Showing top 25 of {data.count}. Export CSV for the full list.
        </div>
      )}
    </div>
  );
};

export default MissingPriceBacklog;
